import React, { useEffect, useMemo } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import { useGetTreeById } from 'reducers/trees_hooks'
import {
  allNodesOfType,
  childNodesOfType,
} from 'components/utility/TreeHelpers'
import { NodeType } from 'shared/types/ingestion'
import { INode } from 'shared/types/tree'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  nodesToCascadeAtom,
  workingIdMapAtom,
  workingTreeAtom,
  treeIdAtom,
  versionIdAtom,
  standardsSelectedAtom,
} from './atoms'
import { openNode } from 'components/utility/TreeNavigation'
import {
  filterQuestionNoList,
  filterQuestionToApply,
  getMappedStandardsLists,
} from './helpers'
import { standardsContent } from './constants'
import { CascadeStandards } from './CascadeStandards'
import { CreateSkillsList } from './CreateSkillsList'

export function StandardsCascadeTool({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const { tree, resetTree } = useGetTreeById({ treeId, versionId })
  const [nodeTitle, setNodeTitle] = React.useState('')
  const [_, setWorkingTree] = useAtom(workingTreeAtom)
  const [standardsSelected, setStandardsSelected] = useAtom(
    standardsSelectedAtom,
  )
  const [nodestoCascade, setNodesToCascade] = useAtom(nodesToCascadeAtom)
  const setTreeId = useSetAtom(treeIdAtom)
  const setVersionId = useSetAtom(versionIdAtom)
  const [resetKey, setResetKey] = React.useState(0)
  const columns = useTableColumns()

  // Initialize atoms with props
  React.useEffect(() => {
    setTreeId(treeId)
    setVersionId(versionId)
    setStandardsSelected(standardsContent.STANDARDS.toUpperCase())
  }, [treeId, versionId])

  const filterNodesByTitle = (title: string) => {
    if (!tree) return []
    // Filter nodes by title
    const nodes =
      allNodesOfType(
        tree,
        NodeType.LessonPlan,
        NodeType.Activity,
        NodeType.Assessment,
      ) || []
    const filteredNodes =
      nodes.filter((node) => {
        const hasTitle = node.title.toUpperCase().includes(title.toUpperCase())
        const hasSelectedStandard =
          childNodesOfType(node, NodeType.SkillsList).filter((list) =>
            list.title.toUpperCase().includes(standardsSelected),
          ).length > 0
        return hasTitle && hasSelectedStandard
      }) || []
    setNodesToCascade(filteredNodes)
  }

  useEffect(() => {
    if (!tree) return
    const nodes =
      childNodesOfType(
        tree,
        NodeType.LessonPlan,
        NodeType.Activity,
        NodeType.Assessment,
      ) || []
    setNodesToCascade(nodes)
    setWorkingTree({ tree, resetTree })
  }, [tree])

  return (
    <Box>
      <Alert severity="info">
        <AlertTitle>Standards Cascade Tool</AlertTitle>
        This tool allows you to cascade standards from one assignable component
        to another their internal questions.
        <ul>
          <li>
            This is a <strong>permanent</strong> change on the tree
          </li>
          <li>
            Make sure the titles of the Lesson/Activities to cascade from Match
            the titles
          </li>
        </ul>
      </Alert>
      <Paper elevation={3}>
        <Box
          component={'form'}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 4, // Spacing between the two form elements
            width: 'auto',
            padding: 2,
          }}
        >
          <FormControl fullWidth>
            <InputLabel htmlFor="node-title">
              Title of Lesson/Activities starting with
            </InputLabel>
            <Input
              type="text"
              id={'node-title'}
              onChange={(e) => setNodeTitle(e.target.value)}
            />
          </FormControl>
          <FormControl fullWidth>
            <InputLabel html-for={'standards-select'}>
              Select Standards Content to cascade from
            </InputLabel>
            <Select
              id="standards-select"
              label="Select Standards Content to cascade from"
              onChange={(e) => {
                setStandardsSelected(e.target.value)
              }}
              value={standardsSelected}
            >
              {Object.entries(standardsContent).map(([key, content]) => (
                <MenuItem key={key} value={content.toUpperCase()}>
                  {content}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack id="actions" width={'100%'} gap={2}>
            <Button
              variant="contained"
              onClick={() => {
                filterNodesByTitle(nodeTitle)
              }}
            >
              Search Standards
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                if (resetTree) {
                  resetTree()
                }
                setNodesToCascade([])
                setResetKey((prev) => prev + 1)
              }}
            >
              Reset Tree
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Box padding={3}>
        <Typography>
          Showing{' '}
          <Tooltip title="Lessons, Activities, Assesments ">
            <em>Assignable nodes</em>
          </Tooltip>{' '}
          that start with the given title and have skill lists with the selected
          standards name
        </Typography>
      </Box>
      <Table
        columns={columns}
        items={nodestoCascade}
        getRowStyle={(index: number) => {
          const node = nodestoCascade[index]
          if (!node) return undefined

          const questionsWithoutSkillsLists = filterQuestionNoList(node).length
          const questionsToCascade = filterQuestionToApply(node).length

          if (questionsWithoutSkillsLists > 0 || questionsToCascade > 0) {
            return {
              backgroundColor: '#fff3cd',
            }
          }
          return undefined
        }}
      />
      <CreateSkillsList
        key={`create-skills-${resetKey}`}
        treeId={treeId}
        versionId={versionId}
      />
      <CascadeStandards
        key={`cascade-standards-${resetKey}`}
        standardsSelected={standardsSelected}
        treeId={treeId}
        versionId={versionId}
      />
    </Box>
  )
}

export function useTableColumns() {
  const idMap = useAtomValue(workingIdMapAtom)
  const nodesToCascade = useAtomValue(nodesToCascadeAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Node Title</strong>,
        width: 100,
        rowContent({ title }: INode) {
          return title
        },
      },
      {
        title: <strong>Node Type</strong>,
        width: 50,
        rowContent({ type }: INode) {
          return type
        },
      },
      {
        title: <strong>Open</strong>,
        width: 50,
        rowContent(node: INode) {
          return <Button onClick={() => openNode(node, idMap)}>Go</Button>
        },
      },
      {
        title: <strong>Standards Content</strong>,
        width: 100,
        rowContent(node: INode) {
          const standardsLists = childNodesOfType(node, NodeType.SkillsList)
          const standardsString = standardsLists
            .map((list) => list.title)
            .join(', ')
          return standardsString
        },
      },
      {
        title: <strong>Questions</strong>,
        width: 25,
        rowContent(node: INode) {
          const questions = allNodesOfType(node, NodeType.Question)
          return questions.length
        },
      },
      {
        title: <strong>Questions Without Skills Lists</strong>,
        width: 25,
        rowContent(node: INode) {
          const filtered = filterQuestionNoList(node)
          return filtered.length
        },
      },
      {
        title: <strong>Questions to Cascade Standards</strong>,
        width: 25,
        rowContent: (node: INode) => {
          const filteredQuestions = filterQuestionToApply(node)

          return filteredQuestions.length
        },
      },
    ]
  }, [nodesToCascade])
}
